/**
 * The bottom sheet's three heights, and the arithmetic that decides which one a
 * drag ends in. Pure, so the rule can be tested without a browser — the panel
 * itself only has to draw it.
 */

/** one row (the summary), half the map, or all of it */
export type SheetState = 'collapsed' | 'half' | 'full';

export const SHEET_ORDER: readonly SheetState[] = ['collapsed', 'half', 'full'];
/** the shortest the sheet ever gets while dragging: the grab row itself */
export const SHEET_MIN_PX = 44;
/** a pull shorter than this is a nudge, not an instruction */
export const SHEET_INTENT_PX = 40;

export const LS_SHEET = 'avia.map.sheet';

export const readSheet = (): SheetState => {
  try {
    const v = localStorage.getItem(LS_SHEET);
    if (v === 'collapsed' || v === 'half' || v === 'full') return v;
  } catch {
    /* private mode */
  }
  return 'half';
};

export const writeSheet = (state: SheetState): void => {
  try {
    localStorage.setItem(LS_SHEET, state);
  } catch {
    /* the choice simply does not survive the tab */
  }
};

/** The next height when the grab bar is tapped rather than dragged. */
export const cycleSheet = (from: SheetState): SheetState =>
  SHEET_ORDER[(SHEET_ORDER.indexOf(from) + 1) % SHEET_ORDER.length] ?? 'half';

/**
 * Where a drag lands.
 *
 * @param from   the height the drag started from
 * @param height the sheet's height when the finger was lifted, in px
 * @param max    the height of the map pane the sheet lives in, in px
 * @param dy     how far the finger travelled, positive = upwards = grow
 *
 * The fractions pick the nearest of the three heights, but a deliberate pull
 * always moves at least one step in its own direction: 180 px up a tall tablet
 * is still under a quarter of the screen, and snapping back to where it came
 * from would read as the sheet ignoring you.
 */
export function snapSheet(from: SheetState, height: number, max: number, dy: number): SheetState {
  const frac = height / Math.max(1, max);
  let next: SheetState = frac < 0.25 ? 'collapsed' : frac < 0.72 ? 'half' : 'full';
  if (Math.abs(dy) > SHEET_INTENT_PX) {
    const i = SHEET_ORDER.indexOf(from);
    const j = SHEET_ORDER.indexOf(next);
    if (dy > 0 && j <= i) next = SHEET_ORDER[Math.min(i + 1, SHEET_ORDER.length - 1)] ?? next;
    if (dy < 0 && j >= i) next = SHEET_ORDER[Math.max(i - 1, 0)] ?? next;
  }
  return next;
}
